import { useEffect, useState } from "react";
import { UserPlus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["full_name", "Full Legal Name *", "text"],
  ["phone", "Phone Number *", "text"],
  ["emergency_contact", "Emergency Contact *", "text"],
  ["bank_name", "Bank Name *", "text"],
  ["bank_account", "Bank Account Number *", "text"],
  ["date_of_hire", "Date of Hire *", "date"],
  ["base_salary", "Base Salary (RM / month) *", "number"],
];

const EMPTY = {
  name: "", email: "", password: "", role: "runner", workshop_company: "",
  full_name: "", phone: "", emergency_contact: "", bank_name: "",
  bank_account: "", date_of_hire: "", base_salary: "",
};

export default function RegisterStaffDialog({ open, onClose, onDone, creatorRole = "admin" }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const isExec = ["ceo", "coceo", "dev"].includes(creatorRole);
  const roleOptions = ["runner", "finance", "admin", ...(isExec ? ["ceo", "coceo"] : [])];

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "workshop" && !form.workshop_company) {
      toast.error("Select the workshop's company from the Inhouse List");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", { ...form, base_salary: parseFloat(form.base_salary) || 0 });
      toast.success(`Account created for ${form.email}`);
      setForm(EMPTY);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-background" data-testid="register-staff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <UserPlus size={22} className="text-primary" /> Register New Staff
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          All fields are mandatory — the account stays locked until the profile is complete.
          Driver accounts additionally require a verified GDL license (upload after creation in the Staff Directory).
        </p>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Display Name *</Label>
              <Input data-testid="reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm border-input bg-card focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email *</Label>
              <Input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm border-input bg-card focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password *</Label>
              <Input data-testid="reg-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="reg-role" className="rounded-sm border-input bg-card focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {roleOptions.map((r) => <SelectItem key={r} value={r} className="uppercase" data-testid={`reg-role-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Driver role unlocks after GDL verification</p>
            </div>
            {FIELDS.map(([key, label, type]) => (
              <div key={key} className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
                <Input data-testid={`reg-${key}`} type={type} required value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
              </div>
            ))}
          </div>
          <Button data-testid="reg-submit" type="submit" disabled={busy}
            className="w-full rounded-sm bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
            {busy ? "Creating..." : "Create Staff Account"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
